import { freeImageRoutingURL } from "./free-image-rollout-routing";
import { isGlideCloudinaryURL, legacyImageRecipe, preservedImageURL } from "@glide/support";
import { getFeatureSetting } from "../feature-settings";
import { getLocationSettings } from "../location";

/** Route before transformation so ImageKit cannot fetch a retired Cloudinary source. */
export function imageSourceURL(src: string, fallback?: string, retire = getFeatureSetting("retireCloudinary")): string {
    return sourceURL(src, true, false, fallback, retire);
}

/** Files and embedded players must retain their original format and provider semantics. */
export function mediaSourceURL(src: string, download: boolean = false, appID?: string): string {
    const original = mediaURL(src, download, getFeatureSetting("retireCloudinary"));
    if (appID === undefined) return original;
    return freeImageRoutingURL(
        appID,
        original,
        () => mediaURL(src, download, true),
        () => `${getLocationSettings().containerURLPrefix}/preservedImage`
    );
}

function mediaURL(src: string, download: boolean, retire: boolean): string {
    const result = sourceURL(src, false, true, undefined, retire);
    if (!download || !retire) return result;
    try {
        const url = new URL(result);
        const endpoint = new URL(`${getLocationSettings().containerURLPrefix}/preservedImage`);
        if (url.origin === endpoint.origin && url.pathname === endpoint.pathname) {
            url.searchParams.delete("preview");
            url.searchParams.set("download", "1");
            return url.href;
        }
    } catch {
        /* Preserve the original navigation behavior. */
    }
    return result;
}

function sourceURL(
    src: string,
    preview: boolean,
    legacyOnly: boolean,
    fallback?: string,
    retire = getFeatureSetting("retireCloudinary")
): string {
    if (!retire) return src;
    try {
        const endpoint = `${getLocationSettings().containerURLPrefix}/preservedImage`;
        const url = new URL(src.startsWith("//") ? `https:${src}` : src);
        if (url.protocol !== "https:" && url.protocol !== "http:") return src;
        if (
            legacyOnly &&
            !isGlideCloudinaryURL(url.href) &&
            url.hostname !== "di.glideapps.com" &&
            !(url.hostname === "ik.imagekit.io" && url.pathname.startsWith("/glideapps/fetch/"))
        )
            return src;
        if (url.origin === new URL(endpoint).origin && url.pathname === new URL(endpoint).pathname) {
            if (preview) url.searchParams.set("preview", "1");
            return url.href;
        }
        // A shared Img can receive an already transformed ImageKit URL. Do not
        // send it back through the resolver, which would repeat the transformation.
        let nested = src;
        for (let depth = 0; depth < 8; depth++) {
            const recipe = legacyImageRecipe(nested);
            if (recipe === undefined) break;
            const inner = new URL(recipe.source);
            if (inner.pathname === "/preservedImage" && inner.origin === new URL(endpoint).origin) return src;
            nested = recipe.source;
        }
        if (url.hostname.endsWith(".internal.glideapps.com")) return src;
        if (src.startsWith("//")) src = url.href;
        const result = new URL(preservedImageURL(src, endpoint));
        if (preview) result.searchParams.set("preview", "1");
        if (fallback !== undefined && fallback !== src) result.searchParams.set("fallback", fallback);
        return result.href;
    } catch {
        return src;
    }
}
